interface PendingPan {
  element: any;
  options: {
    padding?: number;
    duration?: number;
  };
  frameId: number | null;
}

const pendingPanMap = new WeakMap<any, PendingPan>();

/**
 * Cytoscape 视角辅助：只做平移，不改缩放，避免执行过程里画布频繁居中造成迷失。
 *
 * 运行日志密集时会连续触发节点状态/结果/人员变化，这里用 requestAnimationFrame
 * 合并同一帧内的多次请求，只平移到最新目标，避免和节点动画、旋转点动画互相抢帧。
 */
export function panElementIntoView(
  cy: any,
  element: any,
  options: {
    padding?: number;
    duration?: number;
  } = {}
) {
  if (!cy || !element || element.empty?.()) return;
  const pending = pendingPanMap.get(cy);
  if (pending) {
    pending.element = element;
    pending.options = options;
    return;
  }

  const nextPending: PendingPan = {
    element,
    options,
    frameId: window.requestAnimationFrame(() => {
      pendingPanMap.delete(cy);
      applyPanElementIntoView(cy, nextPending.element, nextPending.options);
    }),
  };
  pendingPanMap.set(cy, nextPending);
}

function applyPanElementIntoView(
  cy: any,
  element: any,
  options: {
    padding?: number;
    duration?: number;
  } = {}
) {
  if (!cy || !element || element.empty?.()) return;
  const padding = options.padding ?? 72;
  const duration = options.duration ?? 180;
  const box = element.renderedBoundingBox({ includeLabels: true });
  const width = cy.width();
  const height = cy.height();
  let dx = 0;
  let dy = 0;

  if (box.x1 < padding) {
    dx = padding - box.x1;
  } else if (box.x2 > width - padding) {
    dx = width - padding - box.x2;
  }

  if (box.y1 < padding) {
    dy = padding - box.y1;
  } else if (box.y2 > height - padding) {
    dy = height - padding - box.y2;
  }

  if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;
  const currentPan = cy.pan();
  try {
    cy.animate(
      {
        pan: {
          x: currentPan.x + dx,
          y: currentPan.y + dy,
        },
      },
      { duration }
    );
  } catch (err) {
    // 极端情况下 Cytoscape 动画创建失败时只降级本次平移，不影响画布后续交互。
    console.warn('[cytoscapeViewport] pan animation failed', err);
    cy.pan({
      x: currentPan.x + dx,
      y: currentPan.y + dy,
    });
  }
}
